import z from "zod";

import { ErrorClassification } from "../../../sentry/classification";
import type { ErrorDetails } from "../domain";

const workSiteErrorTypeValues = [
  "WORKSITE_ADDRESS_EDIT_CONFLICT",
  "WORKSITE_ADDRESS_EDIT_DENIED",
  "WORKSITE_ADDRESS_LOOKUP_UNAVAILABLE",
  "WORKSITE_ADDRESS_NOT_FOUND",
  "WORKSITE_ADDRESS_UNIT_REQUIRED",
  "WORKSITE_NOT_ACCESSIBLE",
  "WORKSITE_NOT_FOUND",
  "WORKSITE_SUMMARY_NOT_FOUND",
  "WORKSITE_SUMMARY_UNAVAILABLE",
] as const;
const workSiteErrorTypeSchema = z.enum(workSiteErrorTypeValues);
export const WorkSiteErrorType = workSiteErrorTypeSchema.enum;
type WorkSiteErrorType = z.infer<typeof workSiteErrorTypeSchema>;

export type WorkSiteErrors =
  | { errorType: typeof WorkSiteErrorType.WORKSITE_ADDRESS_EDIT_CONFLICT; data: { workSiteId: number } }
  | {
      errorType: typeof WorkSiteErrorType.WORKSITE_ADDRESS_EDIT_DENIED;
      data: { workSiteId: number; linearIssueUrl: string | null };
    }
  | { errorType: typeof WorkSiteErrorType.WORKSITE_ADDRESS_LOOKUP_UNAVAILABLE; data: { workSiteId: number } }
  | { errorType: typeof WorkSiteErrorType.WORKSITE_ADDRESS_NOT_FOUND; data: { workSiteId: number; role: string } }
  | { errorType: typeof WorkSiteErrorType.WORKSITE_ADDRESS_UNIT_REQUIRED; data: { workSiteId: number | null } }
  | { errorType: typeof WorkSiteErrorType.WORKSITE_NOT_ACCESSIBLE; data: { workSiteId: number } }
  | { errorType: typeof WorkSiteErrorType.WORKSITE_NOT_FOUND; data: { workSiteId: number } }
  | { errorType: typeof WorkSiteErrorType.WORKSITE_SUMMARY_NOT_FOUND; data: { workSiteId: number } }
  | { errorType: typeof WorkSiteErrorType.WORKSITE_SUMMARY_UNAVAILABLE; data: { workSiteId: number } };

export const workSiteErrorDetails: Record<WorkSiteErrorType, ErrorDetails> = {
  WORKSITE_ADDRESS_EDIT_CONFLICT: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Worksite address changed under a concurrent edit",
  },
  WORKSITE_ADDRESS_EDIT_DENIED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription:
      "A bid on this worksite is past pre-production, so its address can't move to a different property. R&D will review the change.",
  },
  WORKSITE_ADDRESS_LOOKUP_UNAVAILABLE: {
    classification: ErrorClassification.RETRYABLE_ERROR,
    sentryDescription: "Address lookup unavailable; a shared address cannot be edited until it returns",
  },
  WORKSITE_ADDRESS_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Work site has no live address in the requested role",
  },
  WORKSITE_ADDRESS_UNIT_REQUIRED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Smarty says the building needs a unit; staff must add one or confirm the whole building",
  },
  WORKSITE_NOT_ACCESSIBLE: {
    classification: ErrorClassification.AUTHORIZATION_ERROR,
    sentryDescription: "Worksite not accessible to caller",
  },
  WORKSITE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Work site not found",
  },
  WORKSITE_SUMMARY_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Work site summary has not been requested yet",
  },
  WORKSITE_SUMMARY_UNAVAILABLE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Work site summary exists but is not readable",
  },
};
